import { ArrowDownRight, ArrowUpRight, ChevronRight, Minus } from 'lucide-react';
import { useId, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { cn } from '../../lib/cn.js';
import { Skeleton } from '../ui/misc.js';

/**
 * The furniture a dashboard is assembled from.
 *
 * Both consoles had grown their own copy of the same four things — a grid
 * of tiles, a titled section with a "see all" beside the heading, a row
 * that is a link, and a period switcher — each with slightly different
 * padding and a different focus ring. They are one implementation here so
 * the two dashboards stay recognisably the same product.
 */

// ── Trend ────────────────────────────────────────────────────

export interface TrendPillProps {
  /**
   * Change as a fraction: 0.25 is a quarter up. Null means the comparison
   * has no answer — a period measured against nothing — and renders as an
   * em dash rather than a made-up hundred per cent.
   */
  ratio: number | null;
  /** What it is being compared with: "vs previous 30 days". */
  caption?: string;
  /**
   * Whether up is good. False for the metrics where it is not —
   * cancellations, time to first reply.
   */
  higherIsBetter?: boolean;
  className?: string;
}

/**
 * A period-on-period delta.
 *
 * Colour is earned by direction *and* by whether that direction is welcome,
 * so a rise in cancellations is not painted green. Under half a per cent is
 * drawn as flat: rounding noise shown as a trend is worse than no trend.
 */
export function TrendPill({
  ratio,
  caption,
  higherIsBetter = true,
  className,
}: TrendPillProps) {
  if (ratio === null) {
    return (
      <span className={cn('text-xs text-text-subtle', className)} title={caption}>
        —
      </span>
    );
  }

  const flat = Math.abs(ratio) < 0.005;
  const good = flat ? null : ratio > 0 === higherIsBetter;
  const Icon = flat ? Minus : ratio > 0 ? ArrowUpRight : ArrowDownRight;

  return (
    <span
      className={cn(
        'inline-flex items-center gap-0.5 text-xs font-semibold tabular-nums',
        good === null ? 'text-text-muted' : good ? 'text-success' : 'text-danger',
        className,
      )}
      title={caption}
    >
      <Icon className="size-3.5" aria-hidden />
      {flat ? '0%' : `${ratio > 0 ? '+' : '−'}${Math.round(Math.abs(ratio) * 100)}%`}
    </span>
  );
}

// ── Grid ─────────────────────────────────────────────────────

export interface StatGridProps {
  children: ReactNode;
  /** How many tiles sit across the widest breakpoint. */
  columns?: 2 | 3 | 4;
  className?: string;
}

/**
 * The row of tiles at the top of a console.
 *
 * Two up on a phone rather than one: these are short numbers, and a single
 * column turns four of them into a scroll before the page has said
 * anything. The class strings are spelled out in full because Tailwind
 * scans source for literals — a template string here compiles to no
 * columns at all.
 */
export function StatGrid({ children, columns = 4, className }: StatGridProps) {
  return (
    <div
      className={cn(
        'grid grid-cols-2 gap-3 sm:gap-4',
        columns === 2 && 'lg:grid-cols-2',
        columns === 3 && 'lg:grid-cols-3',
        columns === 4 && 'lg:grid-cols-2 xl:grid-cols-4',
        className,
      )}
    >
      {children}
    </div>
  );
}

// ── Panel ────────────────────────────────────────────────────

export interface PanelProps {
  title: string;
  /** A count or a period, set small beside the title. */
  subtitle?: string;
  /** Top-right: a "see all" link, a period switcher. */
  action?: ReactNode;
  children: ReactNode;
  /** Swaps the body for skeletons of about the right shape. */
  isLoading?: boolean;
  className?: string;
  bodyClassName?: string;
}

/**
 * A titled section of a dashboard.
 *
 * A bordered card rather than a bare heading: a console is a page of
 * unrelated answers, and on a wide screen the whitespace between two bare
 * lists stops reading as a boundary at about 1400px — which is the width
 * these are actually used at.
 */
export function Panel({
  title,
  subtitle,
  action,
  children,
  isLoading = false,
  className,
  bodyClassName,
}: PanelProps) {
  return (
    <section
      className={cn(
        'flex flex-col rounded-card border border-border-default bg-surface-raised',
        className,
      )}
    >
      <header className="flex items-center justify-between gap-3 px-5 pb-3 pt-4">
        <h2 className="flex min-w-0 items-baseline gap-2">
          <span className="truncate font-display text-base font-bold">{title}</span>
          {subtitle ? (
            <span className="shrink-0 text-xs text-text-muted">{subtitle}</span>
          ) : null}
        </h2>
        {action ? <div className="shrink-0">{action}</div> : null}
      </header>

      <div className={cn('flex-1 px-5 pb-5', bodyClassName)}>
        {isLoading ? (
          <div className="flex flex-col gap-2" aria-hidden>
            <Skeleton className="h-10 rounded-field" />
            <Skeleton className="h-10 rounded-field" />
            <Skeleton className="h-10 w-2/3 rounded-field" />
          </div>
        ) : (
          children
        )}
      </div>
    </section>
  );
}

/** The line a panel shows instead of its list when there is nothing in it. */
export function PanelEmpty({ children }: { children: ReactNode }) {
  return <p className="py-2 text-sm text-text-muted">{children}</p>;
}

// ── Row ──────────────────────────────────────────────────────

export interface ListRowProps {
  to: string;
  title: string;
  meta?: string;
  /** A badge, a time, an amount — set against the chevron. */
  trailing?: ReactNode;
  /** A thumbnail or an icon at the left. */
  leading?: ReactNode;
  className?: string;
}

/**
 * One row of a dashboard queue.
 *
 * The whole row is the target. Work you cannot click is a notification,
 * and a word-sized "Open" at the far right of a phone-width row is a
 * target nobody hits.
 */
export function ListRow({ to, title, meta, trailing, leading, className }: ListRowProps) {
  return (
    <li>
      <Link
        to={to}
        className={cn(
          'flex items-center gap-3 rounded-field border border-border-default px-3 py-2.5 transition-colors hover:border-border-strong hover:bg-surface-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent',
          className,
        )}
      >
        {leading ? <span className="shrink-0">{leading}</span> : null}

        <span className="min-w-0 flex-1">
          <span className="block truncate text-sm font-medium">{title}</span>
          {meta ? <span className="block truncate text-xs text-text-muted">{meta}</span> : null}
        </span>

        <span className="flex shrink-0 items-center gap-2">
          {trailing}
          <ChevronRight className="size-4 text-text-muted" aria-hidden />
        </span>
      </Link>
    </li>
  );
}

// ── Period switcher ──────────────────────────────────────────

export interface SegmentedOption<T extends string> {
  value: T;
  label: string;
}

export interface SegmentedProps<T extends string> {
  options: readonly SegmentedOption<T>[];
  value: T;
  onChange: (value: T) => void;
  /** Names the group for a screen reader: "Period". */
  label: string;
  className?: string;
}

/**
 * The little "30 days / 90 days / year" control.
 *
 * Real radio inputs behind the labels rather than a row of buttons,
 * because that is what this is: one of a set, exactly one chosen. Arrow
 * keys then move between the options and roving focus works without a
 * keydown handler — a `role="radio"` button group has to build both by
 * hand and usually forgets.
 */
export function Segmented<T extends string>({
  options,
  value,
  onChange,
  label,
  className,
}: SegmentedProps<T>) {
  const name = useId();

  return (
    <fieldset
      className={cn(
        'inline-flex items-center gap-0.5 rounded-pill bg-surface-sunken p-0.5',
        className,
      )}
    >
      <legend className="sr-only">{label}</legend>

      {options.map((option) => {
        const selected = option.value === value;

        return (
          <label
            key={option.value}
            className={cn(
              'cursor-pointer rounded-pill px-2.5 py-1 text-xs font-semibold transition-colors',
              'has-[:focus-visible]:outline has-[:focus-visible]:outline-2 has-[:focus-visible]:outline-offset-2 has-[:focus-visible]:outline-accent',
              selected
                ? 'bg-surface-active text-text shadow-soft'
                : 'text-text-muted hover:text-text',
            )}
          >
            <input
              type="radio"
              name={name}
              value={option.value}
              checked={selected}
              onChange={() => onChange(option.value)}
              className="sr-only"
            />
            {option.label}
          </label>
        );
      })}
    </fieldset>
  );
}
